import { useState, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";

import { getWalletState, startCheckout, WalletError, CheckoutError } from "@/lib/api/wallet";
import type { LedgerEntry, WalletState } from "@/lib/api/wallet";
import { useAuth } from "@/lib/auth";

interface WalletSearch {
  session_id?: string;
  cancelled?: string;
}

export const Route = createFileRoute("/wallet")({
  validateSearch: (s: Record<string, unknown>): WalletSearch => ({
    session_id: typeof s.session_id === "string" ? s.session_id : undefined,
    // cancelled=true is JSON-parsed to boolean true by TanStack Router's default parser
    cancelled: s.cancelled === true || s.cancelled === "true" ? "true" : undefined,
  }),
  component: WalletPage,
});

function WalletPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const qc = useQueryClient();
  const [showTierPicker, setShowTierPicker] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const wallet = useInfiniteQuery<
    WalletState,
    WalletError | Error,
    InfiniteData<WalletState>,
    string[],
    string | undefined
  >({
    queryKey: ["wallet"],
    queryFn: ({ pageParam }) => getWalletState(pageParam),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.ledger.next_cursor ?? undefined,
    enabled: !loading && !!user,
    retry: (failureCount, err) =>
      !(err instanceof WalletError && err.kind === "unauthorized") && failureCount < 3,
  });

  useEffect(() => {
    if (search.session_id) {
      void qc.invalidateQueries({ queryKey: ["wallet"] });
    }
  }, [search.session_id, qc]);

  const checkout = useMutation({
    mutationFn: (tier: 1 | 2 | 3) => startCheckout(tier),
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err: unknown) => {
      if (err instanceof CheckoutError && err.kind === "unauthorized") {
        void navigate({ to: "/login", search: { next: "/wallet" } });
      }
    },
  });

  if (!loading && !user) {
    void navigate({ to: "/login", search: { next: "/wallet" } });
    return null;
  }

  if (wallet.error instanceof WalletError && wallet.error.kind === "unauthorized") {
    void navigate({ to: "/login", search: { next: "/wallet" } });
    return null;
  }

  const balance = wallet.data?.pages[0]?.balance_c ?? 0;
  const items: LedgerEntry[] = wallet.data?.pages.flatMap((p) => p.ledger.items ?? []) ?? [];

  return (
    <div className="paper-page" data-testid="wallet-page">
      <div className="wallet">
        {search.session_id && !bannerDismissed && (
          <div
            className="wallet-banner wallet-banner--success"
            role="status"
            data-testid="banner-success"
          >
            Top-up succeeded. Your wallet balance is being updated.
            <button
              type="button"
              className="wallet-banner__dismiss"
              onClick={() => setBannerDismissed(true)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        {search.cancelled === "true" && !bannerDismissed && (
          <div
            className="wallet-banner wallet-banner--info"
            role="status"
            data-testid="banner-cancelled"
          >
            Checkout cancelled — your wallet wasn't charged. Try again?
            <button
              type="button"
              className="wallet-banner__dismiss"
              onClick={() => setBannerDismissed(true)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        <header className="wallet-hero">
          <div className="wallet-hero__main">
            <h1 className="wallet-hero__label">Balance</h1>
            <span className="wallet-hero__value" data-testid="wallet-balance">
              {balance.toLocaleString()}
              <span className="wallet-hero__unit">c</span>
            </span>
          </div>
          <button
            type="button"
            className="pg-btn pg-btn--accent wallet-hero__action"
            onClick={() => setShowTierPicker(true)}
            data-testid="topup-btn"
          >
            Top up
          </button>
        </header>

        {showTierPicker && (
          <TierPicker
            onPick={(tier) => {
              setShowTierPicker(false);
              checkout.mutate(tier);
            }}
            onClose={() => setShowTierPicker(false)}
            pending={checkout.isPending}
          />
        )}

        {checkout.isError && (
          <p role="alert" className="wallet-error" data-testid="checkout-error">
            Couldn't start checkout. Try again.
          </p>
        )}

        <section className="wallet-ledger">
          <h2 className="wallet-ledger__title">Activity</h2>
          {wallet.status === "pending" && (
            <p className="wallet-ledger__status" aria-busy="true" data-testid="ledger-loading">
              Loading…
            </p>
          )}
          {items.length === 0 && wallet.status === "success" && (
            <p className="mkt-empty" data-testid="ledger-empty">
              No activity yet — top up to get started.
            </p>
          )}
          {items.length > 0 && (
            <ul className="wallet-ledger__list" data-testid="ledger-list">
              {items.map((entry) => (
                <LedgerRow key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
          {wallet.hasNextPage && (
            <div className="wallet-ledger__more">
              <button
                type="button"
                className="pg-btn pg-btn--ghost"
                onClick={() => {
                  void wallet.fetchNextPage();
                }}
                disabled={wallet.isFetchingNextPage}
              >
                {wallet.isFetchingNextPage ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const TIERS = [
  { tier: 1 as const, usd: 5, coins: 500, bonus: undefined },
  { tier: 2 as const, usd: 10, coins: 1100, bonus: "+10%" },
  { tier: 3 as const, usd: 50, coins: 6000, bonus: "+20%" },
];

function TierPicker({
  onPick,
  onClose,
  pending,
}: {
  onPick: (tier: 1 | 2 | 3) => void;
  onClose: () => void;
  pending: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="wallet-modal-backdrop" onClick={onClose}>
      <div
        className="wallet-tier-picker"
        role="dialog"
        aria-modal="true"
        aria-label="Choose top-up amount"
        data-testid="tier-picker"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="wallet-tier-picker__title">Top up your wallet</h2>
        <ul className="wallet-tier-grid">
          {TIERS.map((t) => (
            <li key={t.tier}>
              <button
                type="button"
                className="wallet-tier-card"
                onClick={() => onPick(t.tier)}
                disabled={pending}
                data-testid={`tier-card-${t.tier}`}
              >
                <span className="wallet-tier-card__coins">{t.coins.toLocaleString()}c</span>
                <span className="wallet-tier-card__usd">${t.usd}</span>
                {t.bonus && <span className="wallet-tier-card__bonus">{t.bonus} bonus</span>}
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="pg-btn pg-btn--ghost"
          onClick={onClose}
          disabled={pending}
          data-testid="tier-picker-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function LedgerRow({ entry }: { entry: LedgerEntry }) {
  const credit = entry.amount > 0;
  const sign = credit ? "+" : "";
  return (
    <li className="wallet-ledger__row" data-testid="ledger-row">
      <span className="wallet-ledger__kind">{labelForType(entry.type)}</span>
      <time className="wallet-ledger__when" dateTime={entry.created_at}>
        {new Date(entry.created_at).toLocaleString()}
      </time>
      <span className={`wallet-ledger__amount${credit ? " wallet-ledger__amount--credit" : ""}`}>
        {sign}
        {entry.amount.toLocaleString()}c
      </span>
    </li>
  );
}

function labelForType(t: LedgerEntry["type"]): string {
  switch (t) {
    case "stripe_topup":
      return "Top-up";
    case "purchase":
      return "Marketplace purchase";
    case "spawn_deposit":
      return "Automaton spawn";
    case "allocation":
      return "Fund automaton";
    case "refund":
      return "Refund";
    case "dispute":
      return "Dispute";
    case "heartbeat_consume":
      return "Heartbeat";
    case "sale_revenue":
      return "Sale revenue";
    case "marketplace_fee":
      return "Marketplace fee";
    case "dead_seller_proceeds":
      return "Seller proceeds";
    case "signup_grant":
      return "Welcome credit";
  }
}
